/**
 * Client-side .pptx → presentationSpec for the in-app deck viewer.
 *
 * Work-generated decks are real PowerPoint files (python-pptx), not the
 * editor's presentationSpec. Opening them in-app means parsing the zip here
 * and painting shapes/images at their original positions. The editor import
 * path rebuilds a clean-modern layout and is the wrong preview for those files.
 *
 * Cached column/bar/line/pie charts, solid fills, pictures and text retain
 * their placement. SmartArt and theme-token fills remain limited.
 */
import JSZip from 'jszip';
import { chartPreviewFromXml } from './pptx_chart_preview';

export const MAX_PPTX_SLIDES = 50;
export const MAX_PPTX_IMAGES_PER_SLIDE = 8;
export const MAX_PPTX_ELEMENTS_PER_SLIDE = 48;
const EMU_PER_INCH = 914400;
const SLIDE_WIDTH = 13.333;
const SLIDE_HEIGHT = 7.5;

const IMAGE_CONTENT_TYPES = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
};

function codePointOrLiteral(match, codePoint) {
  try {
    return String.fromCodePoint(codePoint);
  } catch (_) {
    return match;
  }
}

export function decodeXmlEntities(text) {
  return String(text || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#x([0-9a-f]+);/gi, (match, hex) => codePointOrLiteral(match, parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (match, dec) => codePointOrLiteral(match, parseInt(dec, 10)))
    .replace(/&amp;/g, '&');
}

function emuToInches(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return n / EMU_PER_INCH;
}

function attr(tag, name) {
  return tag?.match(new RegExp(`\\b${name}="(-?\\d+)"`))?.[1];
}

function parseXfrm(xml) {
  if (!xml) return null;
  const offTag = xml.match(/<a:off\b[^>]*\/?>/)?.[0];
  const extTag = xml.match(/<a:ext\b[^>]*\/?>/)?.[0];
  if (!offTag || !extTag) return null;
  return {
    x: emuToInches(attr(offTag, 'x')),
    y: emuToInches(attr(offTag, 'y')),
    w: emuToInches(attr(extTag, 'cx')),
    h: emuToInches(attr(extTag, 'cy')),
  };
}

function parseSrgb(xml) {
  const match = String(xml || '').match(/<a:srgbClr\b[^>]*\bval="([0-9a-fA-F]{6})"/);
  return match ? match[1].toLowerCase() : null;
}

function luminance(hex) {
  const n = parseInt(hex, 16);
  if (!Number.isFinite(n)) return 1;
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseAlign(xml) {
  const match = String(xml || '').match(/\balgn="(ctr|r|just|l)"/);
  if (match?.[1] === 'ctr') return 'center';
  if (match?.[1] === 'r') return 'right';
  return 'left';
}

function parseFontSize(xml) {
  const child = String(xml || '').match(/<a:sz\b[^>]*\bval="(\d+)"/);
  const attr = String(xml || '').match(/<a:rPr\b[^>]*\bsz="(\d+)"/);
  const raw = child?.[1] || attr?.[1];
  if (!raw) return null;
  const pt = Number(raw) / 100;
  return Number.isFinite(pt) && pt > 0 ? pt : null;
}

function runIsBold(xml) {
  return /<a:rPr\b[^>]*\bb="(?:1|true)"/.test(xml)
    || (/<a:b\b(?:\s[^>]*)?(?:\s*\/>|>)/.test(xml) && !/<a:b\b[^>]*\bval="0"/.test(xml));
}

function paragraphsFromShapeXml(shapeXml) {
  const paragraphs = [];
  const paragraphMatches = shapeXml.match(/<a:p\b[\s\S]*?<\/a:p>/g) || [];
  for (const paragraphXml of paragraphMatches) {
    const runs = paragraphXml.match(/<a:r\b[\s\S]*?<\/a:r>|<a:br\b[^>]*\/?>/g) || [];
    const parts = [];
    let fontSize = null;
    let bold = false;
    let color = null;
    for (const run of runs) {
      if (run.startsWith('<a:br')) { parts.push('\n'); continue; }
      const textMatch = run.match(/<a:t(?:\s[^>]*)?>([\s\S]*?)<\/a:t>/);
      if (!textMatch) continue;
      const text = decodeXmlEntities(textMatch[1]).replace(/[ \t\r]+/g, ' ');
      if (!text) continue;
      parts.push(text);
      if (fontSize == null) fontSize = parseFontSize(run);
      if (!bold) bold = runIsBold(run);
      if (!color) color = parseSrgb(run);
    }
    const textContent = parts.join('').trim();
    if (textContent) {
      paragraphs.push({
        text: textContent,
        fontSize,
        bold,
        color,
        fontFace: decodeXmlEntities(paragraphXml.match(/<a:latin\b[^>]*typeface="([^"]+)"/)?.[1] || ''),
        bullet: /<a:buChar\b|<a:buAutoNum\b/.test(paragraphXml),
        align: parseAlign(paragraphXml),
      });
    }
  }
  return paragraphs;
}

function placeholderType(shapeXml) {
  return shapeXml.match(/<p:ph\b[^>]*type="([^"]+)"/)?.[1] || '';
}

function clampBox(box) {
  if (!box) return { x: 0.4, y: 0.4, w: 12.4, h: 1.2 };
  return {
    x: Math.max(-0.5, Number(box.x) || 0),
    y: Math.max(-0.5, Number(box.y) || 0),
    w: Math.min(SLIDE_WIDTH + 1, Math.max(0.005, Number(box.w) || 1)),
    h: Math.min(SLIDE_HEIGHT + 1, Math.max(0.005, Number(box.h) || 0.4)),
  };
}

function bytesToImageSrc(bytes, contentType) {
  if (typeof Blob !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(new Blob([bytes], { type: contentType }));
  }
  if (typeof Buffer !== 'undefined') {
    return `data:${contentType};base64,${Buffer.from(bytes).toString('base64')}`;
  }
  let binary = '';
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < view.length; i++) binary += String.fromCharCode(view[i]);
  const base64 = typeof btoa === 'function' ? btoa(binary) : '';
  return base64 ? `data:${contentType};base64,${base64}` : '';
}

async function readRels(zip, slideName) {
  const relsName = slideName.replace(/slides\/(slide\d+\.xml)$/, 'slides/_rels/$1.rels');
  const relsFile = zip.files[relsName];
  if (!relsFile) return {};
  const relsXml = await relsFile.async('string');
  const relTargets = {};
  for (const relMatch of relsXml.matchAll(/<Relationship\b[^>]*\/?>/g)) {
    const tag = relMatch[0];
    const id = tag.match(/\bId="([^"]+)"/)?.[1];
    const target = tag.match(/\bTarget="([^"]+)"/)?.[1];
    if (id && target) relTargets[id] = target;
  }
  return relTargets;
}

async function extractImages(zip, slideName, slideXml, makeImageUrl, relTargets) {
  const images = [];
  const picMatches = slideXml.match(/<p:pic\b[\s\S]*?<\/p:pic>/g) || [];
  for (const picXml of picMatches.slice(0, MAX_PPTX_IMAGES_PER_SLIDE)) {
    const embedId = picXml.match(/r:embed="([^"]+)"/)?.[1];
    const target = embedId && relTargets[embedId];
    if (!target) continue;
    const mediaPath = target.startsWith('/') ? target.slice(1) : target.replace(/^(\.\.\/)+/, 'ppt/');
    const mediaFile = zip.files[mediaPath] || zip.files[`ppt/${mediaPath.replace(/^\/+/, '')}`];
    if (!mediaFile) continue;
    const contentType = IMAGE_CONTENT_TYPES[(mediaPath.match(/\.\w+$/)?.[0] || '').toLowerCase()];
    if (!contentType) continue;
    let src = '';
    try {
      const bytes = await mediaFile.async('uint8array');
      src = await makeImageUrl(bytes, contentType);
    } catch (_) {
      src = '';
    }
    if (!src) continue;
    images.push({
      type: 'image',
      src,
      alt: 'Slide image',
      ...clampBox(parseXfrm(picXml)),
    });
  }
  return images;
}

function shapeElements(shapeXml, index, defaultColor) {
  const box = clampBox(parseXfrm(shapeXml));
  const fill = parseSrgb(shapeXml.match(/<p:spPr\b[\s\S]*?<\/p:spPr>/)?.[0] || shapeXml);
  const paragraphs = paragraphsFromShapeXml(shapeXml);
  const type = placeholderType(shapeXml);
  const elements = [];

  if (fill && (!paragraphs.length || (box.w >= 0.08 && box.h >= 0.06))) {
    elements.push({
      id: `shape-${index}`,
      type: 'shape',
      fill: `#${fill}`,
      ...box,
      radius: 0,
    });
  }

  if (!paragraphs.length) return elements;

  const role = (type === 'title' || type === 'ctrTitle')
    ? 'title'
    : (type === 'subTitle' ? 'subtitle' : 'body');
  const first = paragraphs[0];
  const color = first.color || defaultColor;
  const items = paragraphs.map((p) => p.text);

  if (items.length > 1 && role === 'body' && paragraphs.some(p => p.bullet)) {
    elements.push({
      id: `text-${index}`,
      type: 'bullets',
      items: items.slice(0, 12),
      role,
      fontSize: first.fontSize || 22,
      bold: first.bold,
      fontFace: first.fontFace,
      preserveTypography: true,
      color,
      align: first.align,
      ...box,
    });
  } else {
    elements.push({
      id: `text-${index}`,
      type: 'text',
      text: items.join('\n'),
      role,
      fontSize: first.fontSize || (role === 'title' ? 36 : 22),
      bold: first.bold || role === 'title',
      fontFace: first.fontFace,
      preserveTypography: true,
      color,
      align: first.align,
      ...box,
    });
  }
  return elements;
}

// Keep native tables as one element: expanding cells into shapes would hit the
// per-slide element limit and silently discard data in even modest tables.
function tableElement(frame, id) {
  const table = frame.match(/<a:tbl\b[\s\S]*?<\/a:tbl>/)?.[0];
  if (!table) return null;
  const columnWidths = [...table.matchAll(/<a:gridCol\b[^>]*\bw="(\d+)"/g)]
    .slice(0, 64).map(match => Math.max(1, Number(match[1])));
  const rowXml = (table.match(/<a:tr\b[\s\S]*?<\/a:tr>/g) || []).slice(0, 128);
  const rows = rowXml.map(row => (row.match(/<a:tc\b[\s\S]*?<\/a:tc>/g) || []).slice(0, 64)
    .map(cell => paragraphsFromShapeXml(cell).map(p => p.text).join('\n')));
  if (!rows.length || !rows.some(row => row.length)) return null;
  const header = /<a:tblPr\b[^>]*\bfirstRow="(?:1|true)"/.test(table);
  const cellStyles = rowXml.map(row => (row.match(/<a:tc\b[\s\S]*?<\/a:tc>/g) || []).slice(0, 64)
    .map(cell => {
      const first = paragraphsFromShapeXml(cell)[0] || {};
      const fill = parseSrgb(cell.match(/<a:tcPr\b[\s\S]*?<\/a:tcPr>/)?.[0]);
      return { background: fill ? `#${fill}` : undefined, color: first.color ? `#${first.color}` : undefined,
        align: first.align, bold: first.bold, fontSize: first.fontSize };
    }));
  return { id, type: 'table', role: 'table', ...clampBox(parseXfrm(frame)),
    header: header ? rows[0] : [], rows: header ? rows.slice(1) : rows,
    columnWidths, rowHeights: rowXml.map(row => Math.max(1, Number(attr(row.match(/<a:tr\b[^>]*>/)?.[0], 'h')) || 1)),
    cellStyles, preserveLayout: true };
}

function slideBackground(slideXml) {
  const bgXml = slideXml.match(/<p:bg\b[\s\S]*?<\/p:bg>/)?.[0] || '';
  const explicit = parseSrgb(bgXml);
  if (explicit) return explicit;
  const firstShape = slideXml.match(/<p:sp\b[\s\S]*?<\/p:sp>/)?.[0] || '';
  const box = parseXfrm(firstShape);
  if (box && box.x === 0 && box.y === 0 && box.w > 13.2 && box.h >= 7.49) {
    return parseSrgb(firstShape.match(/<p:spPr\b[\s\S]*?<\/p:spPr>/)?.[0]);
  }
  return null;
}

function pickTheme(bgHex) {
  if (bgHex && luminance(bgHex) < 0.45) return 'harbor';
  return 'paper';
}

function defaultInk(bgHex) {
  if (bgHex && luminance(bgHex) < 0.45) return 'f2ede3';
  return '1a1814';
}

/**
 * @param {ArrayBuffer|Uint8Array} data
 * @param {{ fallbackTitle?: string, makeImageUrl?: (bytes: Uint8Array, contentType: string) => (string|Promise<string>) }} [options]
 * @returns {Promise<{ spec: object, objectUrls: string[] }>}
 */
export async function presentationSpecFromPptx(data, options = {}) {
  const fallbackTitle = options.fallbackTitle || 'Presentation';
  const objectUrls = [];
  const makeImageUrl = options.makeImageUrl || ((bytes, contentType) => {
    const src = bytesToImageSrc(bytes, contentType);
    if (src && src.startsWith('blob:')) objectUrls.push(src);
    return src;
  });

  const zip = await JSZip.loadAsync(data);
  const slideEntries = Object.keys(zip.files)
    .map((name) => {
      const match = name.match(/^ppt\/slides\/slide(\d+)\.xml$/);
      return match ? { name, order: parseInt(match[1], 10) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .slice(0, MAX_PPTX_SLIDES);

  const slides = [];
  let deckBg = null;

  for (const entry of slideEntries) {
    const xml = await zip.files[entry.name].async('string');
    const bg = slideBackground(xml);
    if (!deckBg && bg) deckBg = bg;
    const ink = defaultInk(bg || deckBg);
    const relTargets = await readRels(zip, entry.name);
    const elements = [];

    if (bg) {
      elements.push({
        id: `slide-${slides.length + 1}-bg`,
        type: 'shape',
        fill: `#${bg}`,
        x: 0,
        y: 0,
        w: SLIDE_WIDTH,
        h: SLIDE_HEIGHT,
        radius: 0,
      });
    }

    const shapeMatches = xml.match(/<p:sp\b[\s\S]*?<\/p:sp>/g) || [];
    for (let i = 0; i < shapeMatches.length; i++) {
      if (elements.length >= MAX_PPTX_ELEMENTS_PER_SLIDE) break;
      elements.push(...shapeElements(shapeMatches[i], `${slides.length + 1}-${i}`, ink));
    }

    const images = await extractImages(zip, entry.name, xml, makeImageUrl, relTargets);
    for (const image of images) {
      if (elements.length >= MAX_PPTX_ELEMENTS_PER_SLIDE) break;
      elements.push({ ...image, id: `slide-${slides.length + 1}-image-${elements.length}` });
    }

    for (const frame of (xml.match(/<p:graphicFrame\b[\s\S]*?<\/p:graphicFrame>/g) || []).slice(0, 8)) {
      if (elements.length >= MAX_PPTX_ELEMENTS_PER_SLIDE) break;
      const table = tableElement(frame, `table-${slides.length + 1}-${elements.length}`);
      if (table) { elements.push(table); continue; }
      const id = frame.match(/<c:chart\b[^>]*r:id="([^"]+)"/)?.[1];
      const target = relTargets[id];
      // Only embedded package charts; never resolve external relationships.
      const chartPath = target?.startsWith('/') ? target.slice(1) : target?.replace(/^\.\.\//, 'ppt/');
      if (!/^ppt\/charts\/[^/]+\.xml$/.test(chartPath || '') || !zip.files[chartPath]) continue;
      const chart = chartPreviewFromXml(await zip.files[chartPath].async('string'), ink);
      if (chart) elements.push({ ...chart, type: 'image', contentFit: 'contain', id: `chart-${id}`, ...clampBox(parseXfrm(frame)) });
    }

    const titleEl = elements.find((el) => el.role === 'title' && el.text)
      || elements.filter(el => el.type === 'text' && el.fontSize >= 28)
        .sort((a, b) => a.y - b.y || b.fontSize - a.fontSize)[0];
    if (titleEl) titleEl.role = 'title';
    slides.push({
      id: `slide-${slides.length + 1}`,
      layout: 'blank',
      preserveLayout: true,
      title: titleEl?.text || '',
      elements: elements.slice(0, MAX_PPTX_ELEMENTS_PER_SLIDE),
    });
  }

  if (!slides.length) {
    slides.push({
      id: 'slide-1',
      layout: 'title',
      title: fallbackTitle,
      elements: [{
        id: 'slide-1-title',
        type: 'text',
        text: fallbackTitle,
        role: 'title',
        fontSize: 36,
        x: 0.64,
        y: 2.4,
        w: 12,
        h: 1.2,
      }],
    });
  }

  return {
    spec: {
      title: slides[0]?.title || fallbackTitle,
      theme: pickTheme(deckBg),
      preserveLayout: true,
      slides,
    },
    objectUrls,
  };
}

export function revokePptxObjectUrls(urls) {
  if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return;
  for (const url of urls || []) {
    try { URL.revokeObjectURL(url); } catch (_) { /* already revoked */ }
  }
}
